import { Stack, Box, Typography, Paper, Chip, Button } from "@mui/material";
import { useSnackbar } from "notistack";
import {
  useTestMailerConfigMutation,
  type MailerConfigShowData,
} from "@/admin/entities/mailerConfigs";
import { CustomCellDate } from "@/shared/components";

interface MailerConfigDetailsProps {
  mailerConfig: MailerConfigShowData;
}

export function MailerConfigDetails({
  mailerConfig,
}: MailerConfigDetailsProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [testMailerConfig, { loading: testing }] =
    useTestMailerConfigMutation();

  const onTest = async () => {
    if (!mailerConfig) return;
    try {
      await testMailerConfig({ variables: { id: mailerConfig.id } });
      enqueueSnackbar("Тестовое письмо отправлено на ваш email", {
        variant: "success",
      });
    } catch (err) {
      enqueueSnackbar((err as Error).message, { variant: "error" });
    }
  };

  if (!mailerConfig) {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" color="error">
          Мейлер не найден
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h6">{mailerConfig.title}</Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={onTest}
            disabled={testing}
          >
            Тест
          </Button>
        </Stack>

        <Box>
          <Typography variant="body2" color="text.secondary">
            Отправитель (From)
          </Typography>
          <Typography variant="body1">{mailerConfig.from}</Typography>
        </Box>

        <Box>
          <Typography variant="body2" color="text.secondary">
            URL сайта
          </Typography>
          <Typography variant="body1">
            {String(mailerConfig.siteUrl)}
          </Typography>
        </Box>

        {mailerConfig.logo?.url ? (
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Логотип сайта
            </Typography>
            <Box
              component="img"
              src={String(mailerConfig.logo.url)}
              alt=""
              sx={{ maxWidth: 200, maxHeight: 80, objectFit: "contain" }}
            />
          </Box>
        ) : null}

        <Box>
          <Typography variant="body2" color="text.secondary">
            Название сайта
          </Typography>
          <Typography variant="body1">{mailerConfig.siteName}</Typography>
        </Box>

        <Box>
          <Typography variant="body2" color="text.secondary">
            Email сайта
          </Typography>
          <Typography variant="body1">{mailerConfig.siteEmail}</Typography>
        </Box>

        <Box>
          <Typography variant="body2" color="text.secondary">
            ID
          </Typography>
          <Typography variant="body1">{mailerConfig.id}</Typography>
        </Box>

        <Box>
          <Typography variant="body2" color="text.secondary">
            Создан
          </Typography>
          <CustomCellDate date={mailerConfig.createdAt} />
        </Box>

        <Box>
          <Typography variant="body2" color="text.secondary">
            Обновлён
          </Typography>
          <CustomCellDate date={mailerConfig.updatedAt} />
        </Box>

        {mailerConfig.deletedAt ? (
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Статус
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip label="Удалён" color="warning" size="small" />
              <CustomCellDate date={mailerConfig.deletedAt} />
            </Stack>
          </Box>
        ) : null}
      </Stack>
    </Paper>
  );
}
